/**
 * Pictures of Custom View pages, for exporting a Document to Word. The page draws its own picture, inside its frame:
 * its HTML, with the styles the browser computed, goes into an SVG the browser draws onto a canvas, so the picture is
 * the browser's own layout of the page. Nothing in a page comes from outside it (its CSP allows only data: and blob:
 * images and fonts), which is what usually spoils this kind of picture. The drawing is modern-screenshot's, sent to
 * the page only when a picture is wanted.
 */

/** How long the host waits for a picture: the page first waits for its data, files and drawing to finish. */
export const CAPTURE_TIMEOUT_MS = 90_000
/** Larger pictures are refused: a page several screens tall at 3x is around 20MB. */
export const MAX_CAPTURE_BYTES = 50 * 1024 * 1024
const MAX_CAPTURE_SIDE = 10_000

export interface PageCapture {
  /** A PNG. */
  data: ArrayBuffer
  /** The page's size on screen, in CSS pixels. */
  width: number
  height: number
}

export type CaptureReply = PageCapture | { error: string }

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
const isSide = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= MAX_CAPTURE_SIDE

/** A page's answer to a request for its picture: a PNG of a sensible size, or why it couldn't draw one. */
export function captureReply(value: unknown): CaptureReply | null {
  if (typeof value !== 'object' || value === null) return null
  const reply = value as Record<string, unknown>
  if (typeof reply.error === 'string') return { error: reply.error.slice(0, 300) }
  const { data, width, height } = reply
  if (!(data instanceof ArrayBuffer) || data.byteLength > MAX_CAPTURE_BYTES || !isSide(width) || !isSide(height)) {
    return null
  }
  const head = new Uint8Array(data, 0, Math.min(data.byteLength, PNG_SIGNATURE.length))
  if (!PNG_SIGNATURE.every((byte, index) => head[index] === byte)) return null
  return { data, width, height }
}

type Capture = (scale: number) => Promise<PageCapture>
const captures = new WeakMap<Element, Capture>()

/** Lets an export take a picture of the page shown in `element`; returns the function that stops it. */
export function registerCapture(element: Element, capture: Capture): () => void {
  captures.set(element, capture)
  return () => {
    if (captures.get(element) === capture) captures.delete(element)
  }
}

/** A picture of the Custom View page shown in `element`, at `scale` times its size on screen. */
export function capturePage(element: Element, scale: number): Promise<PageCapture> {
  const capture = captures.get(element)
  if (!capture) return Promise.reject(new Error("The Custom View hadn't finished loading."))
  return capture(scale)
}

let library: Promise<string> | null = null

/** modern-screenshot's bundle (it sets window.modernScreenshot), fetched once, the first time a page is pictured.
 * Imported by its path, as d3 is (see libraries.ts). A failed fetch isn't kept, so the next export tries again. */
export function loadCaptureLibrary(): Promise<string> {
  library ??= import('../../../../../node_modules/modern-screenshot/dist/index.js?raw').then(
    (module) => module.default,
    (error) => {
      library = null
      throw error
    }
  )
  return library
}

/**
 * Inside the page: `loading(work)` (morta.loading) and `capture({ library, scale, nonce, busy })`, which waits for the
 * page to finish (no requests to Morta, nothing passed to morta.loading, for a second), then draws it. Plain
 * JavaScript put in every page by the bootstrap (runtime.ts), so it's a string.
 */
export const CAPTURE_SCRIPT = `
const loadingWork = new Set();
// Morta waits for this work before taking a picture of the page, e.g. a model still being drawn.
const loading = (work) => {
  const promise = Promise.resolve(work);
  const settled = promise.then(() => {}, () => {});
  loadingWork.add(settled);
  settled.then(() => loadingWork.delete(settled));
  return promise;
};
const CAPTURE_QUIET_MS = 1000, CAPTURE_WAIT_MS = 60000, MAX_CAPTURE_SIDE = 8192;
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
let screenshot = null;
// The library comes from Morta, as a script with the page's nonce, and is kept out of the page's globals.
const loadScreenshot = (source, nonce) => {
  if (screenshot) return screenshot;
  const script = document.createElement('script');
  script.setAttribute('nonce', nonce);
  script.textContent = source;
  document.head.append(script);
  script.remove();
  screenshot = window.modernScreenshot;
  delete window.modernScreenshot;
  if (!screenshot) throw new Error("Morta couldn't load what it draws the page's picture with.");
  return screenshot;
};
// Until nothing has been running for a second (charts finish their transitions), or a minute has passed.
const settle = async (busy) => {
  const deadline = Date.now() + CAPTURE_WAIT_MS;
  let quietSince = Date.now();
  while (Date.now() < deadline) {
    if (busy() || loadingWork.size) quietSince = Date.now();
    else if (Date.now() - quietSince >= CAPTURE_QUIET_MS) break;
    await pause(100);
  }
  if (document.fonts) await document.fonts.ready;
};
// Images held as blob: URLs can't be fetched (connect-src 'none'), so they're drawn instead.
const imageData = async (url) => {
  if (!url.startsWith('blob:')) return false;
  const image = new Image();
  image.src = url;
  await image.decode();
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  canvas.getContext('2d').drawImage(image, 0, 0);
  return canvas.toDataURL();
};
// Every CSS variable the page defines: in its style sheets (the theme's are on :root), and as the browser computed
// them on the root (Chrome lists them there; Safari doesn't).
const customProperties = () => {
  const names = new Set();
  const add = (style) => { for (let i = 0; i < style.length; i++) if (style[i].startsWith('--')) names.add(style[i]); };
  const walk = (rules) => {
    for (const rule of rules) {
      if (rule.style) add(rule.style);
      if (rule.cssRules) walk(rule.cssRules);
    }
  };
  for (const sheet of document.styleSheets) {
    try { walk(sheet.cssRules); } catch (error) { /* a sheet the page can't read */ }
  }
  add(getComputedStyle(document.documentElement));
  return names;
};
const backgroundOf = (element) => {
  const colour = element && getComputedStyle(element).backgroundColor;
  return colour && colour !== 'transparent' && colour !== 'rgba(0, 0, 0, 0)' ? colour : null;
};
const capture = async ({ library, scale, nonce, busy }) => {
  const draw = loadScreenshot(library, nonce);
  await settle(busy);
  const width = document.documentElement.clientWidth || window.innerWidth;
  const height = window.innerHeight;
  const factor = Math.max(0.1, Math.min(scale, MAX_CAPTURE_SIDE / Math.max(width, height, 1)));
  // The library compares each element's styles with a plain element's, made in a frame of its own; the sandbox
  // allows no frames, so the plain elements go in a shadow root, which the page's styles don't reach. Its CSS
  // variables would, being inherited, so they're unset there: a chart's fill="var(--morta-series-1)" must differ
  // from the plain element's, or its colour is left out of the picture.
  const host = document.createElement('div');
  host.style.cssText = 'all: initial; position: fixed; width: 0; height: 0; overflow: hidden; visibility: hidden';
  const shadow = host.attachShadow({ mode: 'closed' });
  const plain = document.createElement('div');
  customProperties().forEach((name) => plain.style.setProperty(name, 'initial'));
  shadow.append(plain);
  document.documentElement.append(host);
  const sandbox = {
    contentWindow: {
      document: {
        createElement: (name) => document.createElement(name),
        createElementNS: (namespace, name) => document.createElementNS(namespace, name),
        body: plain
      },
      getComputedStyle: (element, pseudo) => getComputedStyle(element, pseudo)
    },
    remove: () => host.remove()
  };
  // Safari draws box shadows wrongly when the library scales the picture, so there the page is zoomed instead.
  const safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const size = safari
    ? { scale: 1, width: width * factor, height: height * factor, style: { zoom: String(factor) } }
    : { scale: factor, width, height };
  try {
    const blob = await draw.domToBlob(document.documentElement, Object.assign({
      type: 'image/png',
      sandbox,
      filter: (node) => node !== host,
      fetchFn: imageData,
      features: { restoreScrollPosition: true },
      // A picture can't scroll: what was scrolled into view is moved into place (restoreScrollPosition), and the
      // box clips it, without the scrollbar's gutter an image would otherwise keep.
      onCloneEachNode: (clone) => {
        if (!clone.style) return;
        for (const property of ['overflow-x', 'overflow-y']) {
          if (/auto|scroll|overlay/.test(clone.style.getPropertyValue(property))) clone.style.setProperty(property, 'hidden');
        }
      },
      backgroundColor: backgroundOf(document.body) || backgroundOf(document.documentElement) || '#ffffff'
    }, size));
    return { data: await blob.arrayBuffer(), width, height };
  } finally {
    host.remove();
  }
};
`
